/* global google */

import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  InfoWindow,
} from "@react-google-maps/api";
import { GOOGLE_INFO } from "../../../utils/constants";
import {
  ALL_LOCATIONS_MARKER_IMAGE_URL,
  getAllLocationsStoreMarkerIcon,
} from "./AllLocationsStoreMarkerIcon";
import { MapInfoBox } from "../../ui";
import styles from "./style.module.scss";

const isValidLatLng = (pt) =>
  pt &&
  typeof pt.lat === "number" &&
  typeof pt.lng === "number" &&
  Number.isFinite(pt.lat) &&
  Number.isFinite(pt.lng);

/** Rough envelope for Canadian retail (excludes bad rows that blow up fitBounds). */
const ROUGH_CA = { minLat: 40, maxLat: 72, minLng: -142, maxLng: -48 };

function inRoughCanada(pt) {
  return (
    pt.lat >= ROUGH_CA.minLat &&
    pt.lat <= ROUGH_CA.maxLat &&
    pt.lng >= ROUGH_CA.minLng &&
    pt.lng <= ROUGH_CA.maxLng
  );
}

function toLatLngPoints(coordinates) {
  return (Array.isArray(coordinates) ? coordinates : [])
    .map((c) => ({ lat: Number(c.lat), lng: Number(c.lng) }))
    .filter(isValidLatLng);
}

function boundsFromPoints(points) {
  const b = new google.maps.LatLngBounds();
  points.forEach((p) => b.extend(p));
  return b;
}

function boundsSpanDeg(bounds) {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  return {
    latSpan: Math.abs(ne.lat() - sw.lat()),
    lngSpan: Math.abs(ne.lng() - sw.lng()),
  };
}

/**
 * Points used only for fitting the camera. If the full set spans "the world",
 * use Canada-plausible points so zoom stays usable; all markers still render.
 */
function pointsForFitBounds(allPoints) {
  if (allPoints.length === 0) return [];
  if (allPoints.length === 1) return allPoints;

  const fullBounds = boundsFromPoints(allPoints);
  const { latSpan, lngSpan } = boundsSpanDeg(fullBounds);

  /* Coast-to-coast Canada can be ~35° lat × ~60° lng; higher spans usually mean bad rows. */
  const MAX_REASONABLE_LAT = 42;
  const MAX_REASONABLE_LNG = 85;

  if (latSpan <= MAX_REASONABLE_LAT && lngSpan <= MAX_REASONABLE_LNG) {
    return allPoints;
  }

  const canada = allPoints.filter(inRoughCanada);
  if (canada.length >= 2) return canada;

  return allPoints;
}

function fitMapToPoints(map, fitPoints) {
  if (fitPoints.length === 0) {
    map.setCenter({ lat: 56.1304, lng: -96 });
    map.setZoom(5);
    return;
  }
  if (fitPoints.length === 1) {
    map.setCenter(fitPoints[0]);
    map.setZoom(11);
    return;
  }

  const bounds = boundsFromPoints(fitPoints);
  const { latSpan, lngSpan } = boundsSpanDeg(bounds);

  if (latSpan > 55 || lngSpan > 110) {
    map.setCenter({ lat: 55, lng: -96 });
    map.setZoom(5);
    return;
  }

  try {
    map.fitBounds(bounds, { top: 56, right: 56, bottom: 56, left: 56 });
  } catch {
    map.fitBounds(bounds);
  }

  window.setTimeout(() => {
    const z = map.getZoom();
    if (typeof z === "number" && z > 16) map.setZoom(16);
    if (typeof z === "number" && z < 3) map.setZoom(3);
  }, 0);
}

/**
 * Map for the Canco All Locations page only: every store marker, no user position.
 */
const AllLocationsMap = ({ coordinates }) => {
  const [markedLocations, setMarkedLocations] = useState(null);
  const [mapRef, setMapRef] = useState(null);
  const [infoWindowData, setInfoWindowData] = useState();
  const [isOpen, setIsOpen] = useState(false);

  const { isLoaded } = useJsApiLoader(GOOGLE_INFO);

  const storeMarkerIcon = useMemo(() => {
    if (!isLoaded) return ALL_LOCATIONS_MARKER_IMAGE_URL;
    return getAllLocationsStoreMarkerIcon();
  }, [isLoaded]);

  const fitCamera = useCallback(
    (map) => {
      const allPts = toLatLngPoints(coordinates);
      const fitPts = pointsForFitBounds(allPts);
      fitMapToPoints(map, fitPts);
    },
    [coordinates]
  );

  const onLoad = (map) => {
    setMapRef(map);
    requestAnimationFrame(() => {
      google.maps.event.trigger(map, "resize");
      fitCamera(map);
    });
    window.setTimeout(() => {
      google.maps.event.trigger(map, "resize");
      fitCamera(map);
    }, 280);
  };

  useEffect(() => {
    const locArray = (coordinates || []).reduce(function (result, data) {
      if (data && Number.isFinite(data.lat) && Number.isFinite(data.lng)) {
        result.push(data);
      }
      return result;
    }, []);
    setMarkedLocations(locArray);
  }, [coordinates]);

  useEffect(() => {
    if (!mapRef) return;
    const id = window.setTimeout(() => {
      google.maps.event.trigger(mapRef, "resize");
      fitCamera(mapRef);
    }, 150);
    return () => window.clearTimeout(id);
  }, [coordinates, mapRef, fitCamera]);

  const handleMarkerClick = (id, lat, lng, address, name, city, province) => {
    mapRef?.panTo({ lat, lng });
    setInfoWindowData({ id, address, name, city, province });
    setIsOpen(true);
  };

  return (
    <div className={styles.mapRoot}>
      {isLoaded && (
        <GoogleMap
          mapContainerClassName={styles.mapContainer}
          mapContainerStyle={{ width: "100%", height: "100%" }}
          onLoad={onLoad}
        >
          {markedLocations &&
            markedLocations.map((data, i) => (
              <Marker
                position={{ lat: data.lat, lng: data.lng }}
                icon={storeMarkerIcon}
                key={data.id ?? i}
                onClick={() => {
                  handleMarkerClick(
                    data.id,
                    data.lat,
                    data.lng,
                    data.address,
                    data.name,
                    data.city?.name ?? data.city,
                    data.province?.name ?? data.province
                  );
                }}
              >
                {isOpen && infoWindowData?.id === data.id && (
                  <InfoWindow
                    onCloseClick={() => {
                      setIsOpen(false);
                    }}
                  >
                    <MapInfoBox data={infoWindowData} />
                  </InfoWindow>
                )}
              </Marker>
            ))}
        </GoogleMap>
      )}
    </div>
  );
};

export default AllLocationsMap;
